import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  ConfigurationScopeSummary,
  HolderConfiguration,
  SettingChange,
  SettingView,
  WriteConfigurationScopeRequest,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { createRoute } from "../../routing/createRoute.js";
import { AlsoChanges } from "./AlsoChanges.js";
import { asChains, entriesBelow, findInChains } from "./configurationChains.js";
import { ConfigurationScopeForm } from "./ConfigurationScopeForm.js";
import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";
import { HolderSettingsPanel } from "./HolderSettingsPanel.js";
import { bothRead, type ReadOutcome } from "./readOutcome.js";
import { writeScope, type ScopeWriteOutcome } from "./scopeWrites.js";
import {
  settingsChangedByMoving,
  type ResolvedSettings,
} from "./settingDifferences.js";
import {
  useHolderConfiguration,
  useScopeEndpoints,
} from "./useHolderConfiguration.js";
import {
  readScopeSettingsFromApi,
  useSettingsOfScopes,
  type ReadScopeSettings,
} from "./useSettingsOfScopes.js";

/** One holder's settings as they are loading or loaded. */
export type HolderSettingsState = {
  configuration: HolderConfiguration | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
};

export type ConfigurationScopeContentProps = {
  /** The id the address names, which the configuration is found by in `scopes`. */
  requestedId: string;
  scopes: readonly ConfigurationScopeSummary[];
  loading: boolean;
  failed: boolean;
  settings: HolderSettingsState;
  /** Whether the changes were saved. */
  onSaveSettings: (changes: readonly SettingChange[]) => Promisable<boolean>;
  onRenameOrMove: (
    body: WriteConfigurationScopeRequest,
  ) => Promisable<ScopeWriteOutcome>;
  /** Reads a configuration's settings, to name what else a move changes. */
  readScopeSettings: ReadScopeSettings;
};

/**
 * One configuration: where it sits in its chain, how many organisations use it,
 * and every setting with where its value comes from. Changes to its settings,
 * and moving it, are confirmed before they are saved; renaming saves straight away.
 */
export const ConfigurationScopeContent = ({
  requestedId,
  scopes,
  loading,
  failed,
  settings,
  onSaveSettings,
  onRenameOrMove,
  readScopeSettings,
}: ConfigurationScopeContentProps) => {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [moving, setMoving] = useState<WriteConfigurationScopeRequest | null>(
    null,
  );
  const [moveError, setMoveError] = useState<string | null>(null);
  const [savingMove, setSavingMove] = useState(false);

  const chains = useMemo(() => asChains(scopes), [scopes]);
  const found = useMemo(
    () => findInChains({ chains, scopeId: requestedId }),
    [chains, requestedId],
  );
  // The configuration a move would put it under; undefined while no move is
  // being confirmed, or when the move is to the top of a chain.
  const newParent =
    moving === null || moving.parentScopeId === null
      ? undefined
      : findInChains({ chains, scopeId: moving.parentScopeId })?.entry;
  const newParentSettings = useSettingsOfScopes({
    scopeIds: newParent === undefined ? [] : [newParent.id],
    read: readScopeSettings,
  });

  const breadcrumbs = (
    <Breadcrumbs>
      <Link component={RouterLink} to={createRoute.admin()}>
        {t("admin.title")}
      </Link>
      <Link component={RouterLink} to={createRoute.adminConfigurationScopes()}>
        {t("admin.configuration.scopesTitle")}
      </Link>
      {found !== undefined && (
        <Typography color="text.primary">{found.entry.name}</Typography>
      )}
    </Breadcrumbs>
  );

  if (loading && scopes.length === 0) {
    return (
      <Stack spacing={2}>
        {breadcrumbs}
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (failed) {
    return (
      <Stack spacing={2}>
        {breadcrumbs}
        <Alert severity="error">
          {t("admin.configuration.scope.loadFailed")}
        </Alert>
      </Stack>
    );
  }

  if (found === undefined) {
    return (
      <Stack spacing={2}>
        {breadcrumbs}
        <Alert severity="warning">
          {t("admin.configuration.scope.notFound")}
        </Alert>
        <Box>
          <Link
            component={RouterLink}
            to={createRoute.adminConfigurationScopes()}
          >
            {t("admin.configuration.scope.backToChains")}
          </Link>
        </Box>
      </Stack>
    );
  }

  const { chain, entry, above } = found;
  const entriesUnder = entriesBelow({ chain, scopeId: entry.id });
  const usedUnder = entriesUnder.filter((each) => each.organisationCount > 0);
  const below = usedUnder.reduce(
    (sum, each) => sum + each.organisationCount,
    0,
  );
  const affected = entry.organisationCount + below;
  // Named while only one configuration under it is used; beyond that the list
  // would outgrow the sentence.
  const onlyUsedUnder = usedUnder.length === 1 ? usedUnder[0] : undefined;
  const appliesTo =
    affected === 0
      ? t("admin.configuration.scope.appliesToNone", { name: entry.name })
      : below === 0
        ? t("admin.configuration.scope.appliesToHere", {
            count: affected,
            name: entry.name,
          })
        : onlyUsedUnder === undefined
          ? t("admin.configuration.scope.appliesToBelow", {
              count: affected,
              here: entry.organisationCount,
              below,
              name: entry.name,
            })
          : t("admin.configuration.scope.appliesToBelowNamed", {
              count: affected,
              here: entry.organisationCount,
              below,
              name: entry.name,
              belowName: onlyUsedUnder.name,
            });

  const movedLevel = newParent === undefined ? 1 : newParent.level + 1;
  const movedWithIt = entriesUnder.map((each) => each.name).join(", ");
  const parentSettings: ReadOutcome<ResolvedSettings> =
    moving === null || moving.parentScopeId === null
      ? { kind: "read", value: null }
      : newParent === undefined
        ? { kind: "failed" }
        : newParentSettings(newParent.id);
  const ownSettings: ReadOutcome<readonly SettingView[]> =
    settings.configuration !== null
      ? { kind: "read", value: settings.configuration.settings }
      : settings.loading
        ? { kind: "reading" }
        : { kind: "failed" };
  const otherMoveChanges = bothRead({
    first: ownSettings,
    second: parentSettings,
    combine: ({ first, second }) =>
      settingsChangedByMoving({ configuration: first, parent: second }),
  });

  const nameOf = (id: string | null) =>
    scopes.find((scope) => scope.id === id)?.name ??
    t("admin.configuration.scope.parent.none");

  const confirmMove = async () => {
    if (moving === null) {
      return;
    }

    setSavingMove(true);
    const outcome = await onRenameOrMove(moving);
    setSavingMove(false);

    match(outcome)
      .with({ kind: "refused" }, ({ message }) => {
        setMoveError(message ?? t("admin.configuration.scope.saveFailed"));
      })
      .with({ kind: "saved" }, () => {
        setMoving(null);
      })
      .exhaustive();
  };

  return (
    <Stack spacing={3}>
      {breadcrumbs}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { sm: "flex-start" },
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            {t("admin.configuration.scope.placement", {
              level: entry.level,
              chain: chain.top.name,
            })}
          </Typography>
          <Typography variant="h5" component="h1">
            {entry.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[...above.map((each) => each.name), entry.name].join(" → ")}
            {" · "}
            {entry.organisationCount === 0 ? (
              t("admin.configuration.scope.notUsedDirectly")
            ) : (
              <Link
                component={RouterLink}
                to={createRoute.adminOrganisationConfiguration(entry.id)}
              >
                {t("admin.configuration.scope.usedByLink", {
                  count: entry.organisationCount,
                })}
              </Link>
            )}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRenaming(true);
            }}
          >
            {t("admin.configuration.scope.rename")}
          </Button>
        </Box>
      </Stack>

      <HolderSettingsPanel
        // Keyed on the configuration, so unsaved changes made to one are never
        // shown, or saved, against the next one the address opens.
        key={entry.id}
        holderName={entry.name}
        configuration={settings.configuration}
        loading={settings.loading}
        saving={settings.saving}
        error={settings.error}
        appliesTo={appliesTo}
        onSave={onSaveSettings}
      />

      <Dialog
        open={renaming}
        onClose={() => {
          setRenaming(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("admin.configuration.scope.renameTitle", { name: entry.name })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <ConfigurationScopeForm
              // Keyed on the configuration, so a dialog left open while the
              // address moves to another one starts from that one's fields
              // rather than saving the first one's onto it.
              key={entry.id}
              scopes={scopes}
              editing={entry}
              hint={t("admin.configuration.scope.moveHint")}
              onSubmit={async (body) => {
                if (body.parentScopeId !== entry.parentScopeId) {
                  setRenaming(false);
                  setMoveError(null);
                  setMoving(body);
                  return { kind: "awaiting_confirmation" };
                }

                const outcome = await onRenameOrMove(body);

                match(outcome)
                  .with({ kind: "saved" }, () => {
                    setRenaming(false);
                  })
                  .with({ kind: "refused" }, () => undefined)
                  .exhaustive();

                return outcome;
              }}
              onCancel={() => {
                setRenaming(false);
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmChangesDialog
        open={moving !== null}
        title={t("admin.configuration.scope.moveTitle", { name: entry.name })}
        confirmLabel={t("admin.configuration.scope.moveConfirm")}
        saving={savingMove}
        error={moveError}
        onCancel={() => {
          setMoving(null);
        }}
        onConfirm={confirmMove}
        confirmWaits={otherMoveChanges.kind === "reading"}
      >
        <Typography variant="body2">
          {t("admin.configuration.scope.moveFrom", {
            from: nameOf(entry.parentScopeId),
            to: nameOf(moving?.parentScopeId ?? null),
          })}
        </Typography>
        {moving !== null && moving.name !== entry.name && (
          <Typography variant="body2">
            {t("admin.configuration.scope.renamedTo", {
              from: entry.name,
              to: moving.name,
            })}
          </Typography>
        )}
        <Typography variant="body2">
          {entriesUnder.length === 0
            ? t("admin.configuration.scope.moveLevel", {
                name: entry.name,
                level: movedLevel,
              })
            : t("admin.configuration.scope.moveLevelWith", {
                name: entry.name,
                level: movedLevel,
                below: movedWithIt,
                count: entriesUnder.length,
              })}
        </Typography>
        <Typography variant="body2">{appliesTo}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t("admin.configuration.settings.unsaved.noAccessChange")}
        </Typography>
        <AlsoChanges changes={otherMoveChanges} />
      </ConfirmChangesDialog>
    </Stack>
  );
};

/** One configuration, loaded and written through the API. */
export const AdminConfigurationScopePage = () => {
  const { scopeId: requestedId = "" } = useParams();
  const [{ data, loading, error }, refetch] = apiHooks.configuration.scopes();
  // Looked up rather than taken from the address, so only a configuration the
  // server listed is written, under the id it listed.
  const scope = data?.scopes.find((each) => each.id === requestedId);
  const endpoints = useScopeEndpoints({
    requestedId,
    listedScopeId: scope?.id,
  });
  const settings = useHolderConfiguration(endpoints);
  const { reload } = settings;

  return (
    <ConfigurationScopeContent
      requestedId={requestedId}
      scopes={data?.scopes ?? []}
      loading={loading}
      failed={error !== undefined && error !== null}
      settings={settings}
      onSaveSettings={settings.change}
      onRenameOrMove={async (body) => {
        if (scope === undefined) {
          return { kind: "refused", message: null };
        }

        const outcome = await writeScope({ scopeId: scope.id, body });

        await match(outcome)
          .with({ kind: "saved" }, async () => {
            // Saved already, so a failed re-read must not turn into a failed
            // save: the page shows that failure itself.
            await refetch().catch(() => undefined);
            // A move changes everything the configuration inherits.
            reload();
          })
          .with({ kind: "refused" }, () => undefined)
          .exhaustive();

        return outcome;
      }}
      readScopeSettings={readScopeSettingsFromApi}
    />
  );
};
